import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Award, Plus, Edit2, Trash2, Search, FileText,
  Users, Building2, Receipt, CheckCircle2, Clock, X,
  Send, ExternalLink, ChevronRight, Layers,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { KpiSkeleton, ListSkeleton } from "../components/Skeletons";
import HelpIcon from "../components/help/HelpIcon";

const TABS = [
  { id: "certifications", label: "Certifications", icon: Award },
  { id: "portage", label: "Contrats de portage", icon: Building2 },
  { id: "inscriptions", label: "Inscriptions remontees", icon: Users },
  { id: "invoices", label: "Factures commissions", icon: Receipt },
];

const STATUS_BADGES = {
  draft: { label: "Brouillon", color: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
  active: { label: "Actif", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  suspended: { label: "Suspendu", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  ended: { label: "Termine", color: "bg-slate-700/30 text-slate-500 border-slate-700/40" },
  sent: { label: "Envoyee", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  paid: { label: "Payee", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  overdue: { label: "En retard", color: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const CURRENT_MONTH = new Date().toISOString().slice(0, 7);

export default function CertificationsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [tab, setTab] = useState("certifications");

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="certifications-page">
      <div className="flex items-start gap-2">
        <PageHeader
          icon={<Award />}
          title="Certifications RS / RNCP"
          subtitle="Gerez vos certifications enregistrees a France Competences et leurs contrats de portage."
          right={null}
        />
        <div className="pt-3"><HelpIcon topic="certifications_rncp" size="md" /></div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#1a1625] border border-white/10 p-1 inline-flex">
          {TABS.map(t => (
            <TabsTrigger
              key={t.id}
              value={t.id}
              data-testid={`cert-tab-${t.id}`}
              className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2"
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="certifications" className="mt-6">
          <CertificationsList api={api} />
        </TabsContent>
        <TabsContent value="portage" className="mt-6">
          <PortageContractsList api={api} />
        </TabsContent>
        <TabsContent value="inscriptions" className="mt-6">
          <PortageInscriptionsView api={api} />
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <PortageInvoicesView api={api} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CERTIFICATIONS LIST
// ═══════════════════════════════════════════════════════════

function CertificationsList({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [manageCert, setManageCert] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/certifications");
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement");
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(c =>
    !search ||
    c.title?.toLowerCase().includes(search.toLowerCase()) ||
    c.code?.toLowerCase().includes(search.toLowerCase())
  );

  const onDelete = async (cert_id) => {
    if (!window.confirm("Supprimer cette certification ?")) return;
    try {
      await api.delete(`/certifications/${cert_id}`);
      toast.success("Certification supprimee");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <div className="space-y-4"><KpiSkeleton count={3} /><ListSkeleton items={4} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="cert-search"
              placeholder="Rechercher une certification..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-[#14101c] border-white/10"
            />
          </div>
          <div className="text-sm text-slate-400" data-testid="cert-count">
            {filtered.length} certification{filtered.length > 1 ? "s" : ""}
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold" data-testid="cert-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle certification
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 bg-[#14101c] rounded-xl border border-white/10">
          <Award className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 mb-2">Aucune certification enregistree</p>
          <p className="text-xs text-slate-500">Creez votre premiere fiche RS ou RNCP pour commencer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cert-grid">
          {filtered.map(c => (
            <CertCard
              key={c.certification_id}
              cert={c}
              onEdit={() => setEditing(c)}
              onDelete={() => onDelete(c.certification_id)}
            />
          ))}
        </div>
      )}

      <CertificationFormDialog
        api={api}
        open={showCreate || !!editing}
        cert={editing}
        onClose={() => { setShowCreate(false); setEditing(null); }}
        onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function CertCard({ cert, onEdit, onDelete, onManage }) {
  return (
    <div
      className="bg-[#14101c] border border-white/10 rounded-xl p-5 hover:border-[#f7c935]/50 transition-all hover:-translate-y-1 group"
      data-testid={`cert-card-${cert.certification_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <Badge variant="outline" className={cert.type === "RNCP" ? "bg-purple-500/10 text-purple-400 border-purple-500/30" : "bg-blue-500/10 text-blue-400 border-blue-500/30"}>
          {cert.type}
        </Badge>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`cert-edit-${cert.certification_id}`}>
            <Edit2 className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`cert-delete-${cert.certification_id}`} className="text-red-400 hover:text-red-300">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      <div className="text-xs text-[#f7c935] font-mono mb-1.5">{cert.code}</div>
      <h3 className="font-semibold text-white text-base mb-2 line-clamp-2">{cert.title}</h3>
      {cert.niveau && <p className="text-xs text-slate-400 mb-3">{cert.niveau}</p>}
      <div className="flex items-center gap-4 text-xs text-slate-500 pt-3 border-t border-white/10">
        <div className="flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5" />
          <span>{cert.blocs_count || 0} bloc{(cert.blocs_count || 0) > 1 ? "s" : ""}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" />
          <span>{cert.active_portages_count || 0} portage{(cert.active_portages_count || 0) > 1 ? "s" : ""}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          <span>{cert.candidates_count || 0} candidat{(cert.candidates_count || 0) > 1 ? "s" : ""}</span>
        </div>
        {cert.prix_certificateur_eur > 0 && (
          <div className="ml-auto font-semibold text-emerald-400">{cert.prix_certificateur_eur.toLocaleString("fr-FR")} €</div>
        )}
      </div>
      <Button onClick={onManage} variant="ghost" size="sm" className="w-full mt-3 text-[#f7c935] hover:bg-[#f7c935]/10" data-testid={`cert-manage-${cert.certification_id}`}>
        Gerer candidats &amp; jurys <ChevronRight className="w-3.5 h-3.5 ml-1" />
      </Button>
    </div>
  );
}

function CertificationFormDialog({ api, open, cert, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: "RNCP", code: "", title: "", description: "", niveau: "",
    nsf_code: "", formacode: "", france_competences_url: "",
    prix_certificateur_eur: 0, program_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (cert) {
      setForm({
        type: cert.type || "RNCP", code: cert.code || "", title: cert.title || "",
        description: cert.description || "", niveau: cert.niveau || "",
        nsf_code: cert.nsf_code || "", formacode: cert.formacode || "",
        france_competences_url: cert.france_competences_url || "",
        prix_certificateur_eur: cert.prix_certificateur_eur || 0,
        program_id: cert.program_id || "",
      });
    } else {
      setForm({ type: "RNCP", code: "", title: "", description: "", niveau: "", nsf_code: "", formacode: "", france_competences_url: "", prix_certificateur_eur: 0, program_id: "" });
    }
    api.get("/lms/programs").then(r => setPrograms(r.data || [])).catch(() => setPrograms([]));
  }, [open, cert, api]);

  const submit = async () => {
    if (!form.code || !form.title) { toast.error("Code et titre requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, prix_certificateur_eur: parseFloat(form.prix_certificateur_eur) || 0 };
      if (!payload.program_id) delete payload.program_id;
      if (cert) {
        await api.put(`/certifications/${cert.certification_id}`, payload);
        toast.success("Certification mise a jour");
      } else {
        await api.post("/certifications", payload);
        toast.success("Certification creee");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#14101c] border-white/10 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">{cert ? "Modifier" : "Nouvelle"} certification</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Type *</label>
              <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="cert-form-type" className="bg-[#1a1625] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1625] border-white/10">
                  <SelectItem value="RNCP">RNCP (Registre National)</SelectItem>
                  <SelectItem value="RS">RS (Repertoire Specifique)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Code France Competences *</label>
              <Input data-testid="cert-form-code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="RNCP38765" className="bg-[#1a1625] border-white/10" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Intitule *</label>
            <Input data-testid="cert-form-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Manager strategique d'entreprise" className="bg-[#1a1625] border-white/10" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description</label>
            <Textarea data-testid="cert-form-description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="bg-[#1a1625] border-white/10" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Niveau</label>
              <Input value={form.niveau} onChange={e => setForm(f => ({ ...f, niveau: e.target.value }))} placeholder="Niveau 7" className="bg-[#1a1625] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Code NSF</label>
              <Input value={form.nsf_code} onChange={e => setForm(f => ({ ...f, nsf_code: e.target.value }))} placeholder="310" className="bg-[#1a1625] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Formacode</label>
              <Input value={form.formacode} onChange={e => setForm(f => ({ ...f, formacode: e.target.value }))} placeholder="32140" className="bg-[#1a1625] border-white/10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">URL France Competences</label>
              <Input value={form.france_competences_url} onChange={e => setForm(f => ({ ...f, france_competences_url: e.target.value }))} placeholder="https://www.francecompetences.fr/..." className="bg-[#1a1625] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Prix certificateur HT (€)</label>
              <Input data-testid="cert-form-prix" type="number" step="0.01" value={form.prix_certificateur_eur} onChange={e => setForm(f => ({ ...f, prix_certificateur_eur: e.target.value }))} className="bg-[#1a1625] border-white/10" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Programme LMS rattache</label>
            <Select value={form.program_id || "_none_"} onValueChange={v => setForm(f => ({ ...f, program_id: v === "_none_" ? "" : v }))}>
              <SelectTrigger className="bg-[#1a1625] border-white/10"><SelectValue placeholder="Selectionner un programme" /></SelectTrigger>
              <SelectContent className="bg-[#1a1625] border-white/10">
                <SelectItem value="_none_">— Aucun —</SelectItem>
                {programs.map(p => <SelectItem key={p.program_id} value={p.program_id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Le programme LMS qui sera partage aux centres partenaires lors du portage.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="cert-form-save" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold">
            {cert ? "Enregistrer" : "Creer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// MANAGE DIALOG : candidats + jurys + import blocs XML
// ═══════════════════════════════════════════════════════════

function ManageCertDialog({ api, cert, onClose, onChange }) {
  const [tab, setTab] = useState("candidates");
  const [candidates, setCandidates] = useState([]);
  const [jurys, setJurys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCandidate, setShowCandidate] = useState(false);
  const [showJury, setShowJury] = useState(false);
  const [importingXml, setImportingXml] = useState(false);

  const load = useCallback(async () => {
    if (!cert) return;
    setLoading(true);
    try {
      const [c, j] = await Promise.all([
        api.get(`/certifications/${cert.certification_id}/candidates`),
        api.get(`/certifications/${cert.certification_id}/jurys`),
      ]);
      setCandidates(c.data || []);
      setJurys(j.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, [api, cert]);

  useEffect(() => { if (cert) load(); }, [cert, load]);

  const importXml = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImportingXml(true);
    try {
      const { data } = await api.post(`/certifications/${cert.certification_id}/blocs/import-xml`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported_count} bloc${data.imported_count > 1 ? "s" : ""} importe${data.imported_count > 1 ? "s" : ""}`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur import XML"); }
    setImportingXml(false);
  };

  if (!cert) return null;

  return (
    <Dialog open={!!cert} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#14101c] border-white/10 max-w-4xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-3">
            <Award className="w-5 h-5 text-[#f7c935]" />
            <div>
              <span className="text-xs text-[#f7c935] font-mono mr-2">{cert.code}</span>
              <span>{cert.title}</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#1a1625] border border-white/10 p-1 inline-flex">
            <TabsTrigger value="candidates" data-testid="manage-tab-candidates" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2"><Users className="w-4 h-4" /> Candidats ({candidates.length})</TabsTrigger>
            <TabsTrigger value="jurys" data-testid="manage-tab-jurys" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2"><Layers className="w-4 h-4" /> Jurys ({jurys.length})</TabsTrigger>
            <TabsTrigger value="import" data-testid="manage-tab-import" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-black gap-2"><FileText className="w-4 h-4" /> Import XML</TabsTrigger>
          </TabsList>

          <TabsContent value="candidates" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-400">{candidates.length} candidat{candidates.length > 1 ? "s" : ""}</span>
              <Button onClick={() => setShowCandidate(true)} size="sm" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold" data-testid="add-candidate-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Inscrire un candidat
              </Button>
            </div>
            {loading ? <ListSkeleton items={3} /> : candidates.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm">Aucun candidat inscrit</div>
            ) : (
              <div className="space-y-2" data-testid="candidates-list">
                {candidates.map(c => {
                  const valides = (c.parcours_blocs || []).filter(p => p.status === "valide").length;
                  const total = (c.parcours_blocs || []).length || 1;
                  return (
                    <div key={c.candidate_id} className="bg-[#1a1625] border border-white/5 rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`candidate-row-${c.candidate_id}`}>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-white">{c.student_name}</div>
                        <div className="text-xs text-slate-500">{c.student_email} · Mode {c.mode_validation}</div>
                      </div>
                      <Badge className={c.cert_status === "certified" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : c.cert_status === "partial" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-slate-500/10 text-slate-400 border-slate-500/30"}>
                        {c.cert_status}
                      </Badge>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Blocs</div>
                        <div className="text-sm font-bold text-white">{valides}/{total}</div>
                      </div>
                      <Link to={`/mon-parcours-certifiant/${c.candidate_id}`} className="text-[#f7c935] hover:underline text-xs" data-testid={`candidate-view-${c.candidate_id}`}>
                        Voir
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="jurys" className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-slate-400">{jurys.length} jury{jurys.length > 1 ? "s" : ""}</span>
              <Button onClick={() => setShowJury(true)} size="sm" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold" data-testid="add-jury-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Convoquer un jury
              </Button>
            </div>
            {loading ? <ListSkeleton items={2} /> : jurys.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm">Aucun jury planifie</div>
            ) : (
              <div className="space-y-2" data-testid="jurys-list">
                {jurys.map(j => (
                  <div key={j.jury_id} className="bg-[#1a1625] border border-white/5 rounded-lg p-3" data-testid={`jury-row-${j.jury_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-white">{j.session_label}</div>
                      <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/30">{j.status}</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div><span className="text-slate-500">Date</span><div className="text-slate-300">{j.convocation_date}</div></div>
                      <div><span className="text-slate-500">Modalite</span><div className="text-slate-300">{j.modalite}</div></div>
                      <div><span className="text-slate-500">Membres</span><div className="text-slate-300">{(j.members || []).length}</div></div>
                    </div>
                    <div className="text-xs text-slate-500 mt-2">{(j.candidates_ids || []).length} candidat{(j.candidates_ids || []).length > 1 ? "s" : ""} convoque{(j.candidates_ids || []).length > 1 ? "s" : ""}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="import" className="mt-4">
            <div className="bg-[#1a1625] rounded-xl border border-white/5 p-6">
              <h4 className="text-sm font-semibold text-white mb-2">Import XML France Competences</h4>
              <p className="text-xs text-slate-400 mb-4">Selectionnez un fichier XML exporte depuis le portail France Competences. Les blocs de competences seront extraits automatiquement (avec leur code, libelle et descriptif). Les blocs deja presents ne seront pas dupliques.</p>
              <Input
                type="file"
                accept=".xml"
                onChange={(e) => importXml(e.target.files?.[0])}
                disabled={importingXml}
                data-testid="import-xml-input"
                className="bg-[#14101c] border-white/10 text-slate-300 file:bg-[#f7c935] file:text-black file:border-0 file:rounded file:px-3 file:py-1 file:mr-3"
              />
              {importingXml && <p className="text-xs text-[#f7c935] mt-2">Import en cours...</p>}
              <div className="mt-4 text-xs text-slate-500">Blocs actuels : {cert.blocs_count || 0}</div>
            </div>
          </TabsContent>
        </Tabs>

        <CandidateFormDialog api={api} cert={cert} open={showCandidate} onClose={() => setShowCandidate(false)} onSaved={() => { setShowCandidate(false); load(); }} />
        <JuryFormDialog api={api} cert={cert} candidates={candidates} open={showJury} onClose={() => setShowJury(false)} onSaved={() => { setShowJury(false); load(); }} />
      </DialogContent>
    </Dialog>
  );
}

function CandidateFormDialog({ api, cert, open, onClose, onSaved }) {
  const [form, setForm] = useState({ student_name: "", student_email: "", mode_validation: "complete" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ student_name: "", student_email: "", mode_validation: "complete" }); }, [open]);

  const submit = async () => {
    if (!form.student_name) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      await api.post(`/certifications/${cert.certification_id}/candidates`, { ...form, certification_id: cert.certification_id });
      toast.success("Candidat inscrit");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#14101c] border-white/10 max-w-md">
        <DialogHeader><DialogTitle className="text-white">Inscrire un candidat</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><label className="text-xs text-slate-400 mb-1 block">Nom complet *</label><Input data-testid="candidate-name-input" value={form.student_name} onChange={e => setForm(f => ({ ...f, student_name: e.target.value }))} className="bg-[#1a1625] border-white/10" /></div>
          <div><label className="text-xs text-slate-400 mb-1 block">Email</label><Input type="email" value={form.student_email} onChange={e => setForm(f => ({ ...f, student_email: e.target.value }))} className="bg-[#1a1625] border-white/10" /></div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Mode de validation</label>
            <Select value={form.mode_validation} onValueChange={v => setForm(f => ({ ...f, mode_validation: v }))}>
              <SelectTrigger className="bg-[#1a1625] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1a1625] border-white/10">
                <SelectItem value="complete">Validation complete</SelectItem>
                <SelectItem value="VAE">VAE (experience)</SelectItem>
                <SelectItem value="VAP">VAP (acquis professionnels)</SelectItem>
                <SelectItem value="parcours_partiel">Parcours partiel (1 ou plusieurs blocs)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="candidate-submit" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold">Inscrire</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JuryFormDialog({ api, cert, candidates, open, onClose, onSaved }) {
  const [form, setForm] = useState({ session_label: "", convocation_date: "", location: "", modalite: "presentiel", members: [], candidates_ids: [] });
  const [saving, setSaving] = useState(false);
  const [memberDraft, setMemberDraft] = useState({ full_name: "", email: "", role: "examinateur", qualite: "" });

  useEffect(() => { if (open) setForm({ session_label: "", convocation_date: "", location: "", modalite: "presentiel", members: [], candidates_ids: [] }); }, [open]);

  const addMember = () => {
    if (!memberDraft.full_name) return;
    setForm(f => ({ ...f, members: [...f.members, { ...memberDraft }] }));
    setMemberDraft({ full_name: "", email: "", role: "examinateur", qualite: "" });
  };

  const submit = async () => {
    if (!form.session_label || !form.convocation_date) { toast.error("Session et date requises"); return; }
    setSaving(true);
    try {
      await api.post(`/certifications/${cert.certification_id}/jurys`, { ...form, certification_id: cert.certification_id });
      toast.success("Jury cree");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#14101c] border-white/10 max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white">Convoquer un jury</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400 mb-1 block">Session *</label><Input data-testid="jury-session" value={form.session_label} onChange={e => setForm(f => ({ ...f, session_label: e.target.value }))} placeholder="Session juin 2026" className="bg-[#1a1625] border-white/10" /></div>
            <div><label className="text-xs text-slate-400 mb-1 block">Date *</label><Input data-testid="jury-date" type="date" value={form.convocation_date} onChange={e => setForm(f => ({ ...f, convocation_date: e.target.value }))} className="bg-[#1a1625] border-white/10" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-slate-400 mb-1 block">Modalite</label>
              <Select value={form.modalite} onValueChange={v => setForm(f => ({ ...f, modalite: v }))}>
                <SelectTrigger className="bg-[#1a1625] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1625] border-white/10">
                  <SelectItem value="presentiel">Presentiel</SelectItem>
                  <SelectItem value="distanciel">Distanciel</SelectItem>
                  <SelectItem value="hybride">Hybride</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><label className="text-xs text-slate-400 mb-1 block">Lieu</label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Adresse ou lien visio" className="bg-[#1a1625] border-white/10" /></div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Membres du jury</label>
            <div className="space-y-2 mb-2">
              {form.members.map((m, i) => (
                <div key={i} className="flex items-center gap-2 bg-[#1a1625] border border-white/5 rounded p-2 text-xs">
                  <span className="text-slate-200 flex-1">{m.full_name} <span className="text-slate-500">· {m.role}</span></span>
                  <Button size="sm" variant="ghost" onClick={() => setForm(f => ({ ...f, members: f.members.filter((_, idx) => idx !== i) }))} className="text-red-400 h-6 w-6 p-0"><X className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-12 gap-2">
              <Input className="col-span-4 bg-[#1a1625] border-white/10 text-xs" placeholder="Nom" value={memberDraft.full_name} onChange={e => setMemberDraft(d => ({ ...d, full_name: e.target.value }))} />
              <Input className="col-span-3 bg-[#1a1625] border-white/10 text-xs" placeholder="Email" value={memberDraft.email} onChange={e => setMemberDraft(d => ({ ...d, email: e.target.value }))} />
              <Select value={memberDraft.role} onValueChange={v => setMemberDraft(d => ({ ...d, role: v }))}>
                <SelectTrigger className="col-span-3 bg-[#1a1625] border-white/10 text-xs h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1625] border-white/10">
                  <SelectItem value="president">President</SelectItem>
                  <SelectItem value="examinateur">Examinateur</SelectItem>
                  <SelectItem value="professionnel">Professionnel</SelectItem>
                  <SelectItem value="enseignant">Enseignant</SelectItem>
                  <SelectItem value="observateur">Observateur</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={addMember} size="sm" className="col-span-2 bg-[#f7c935] hover:bg-[#e5b825] text-black"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          {candidates.length > 0 && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Candidats convoques ({form.candidates_ids.length})</label>
              <div className="max-h-32 overflow-y-auto bg-[#1a1625] border border-white/10 rounded">
                {candidates.map(c => (
                  <label key={c.candidate_id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.candidates_ids.includes(c.candidate_id)} onChange={() => setForm(f => ({ ...f, candidates_ids: f.candidates_ids.includes(c.candidate_id) ? f.candidates_ids.filter(id => id !== c.candidate_id) : [...f.candidates_ids, c.candidate_id] }))} className="accent-[#f7c935]" />
                    <span className="text-slate-200">{c.student_name}</span>
                    <span className="text-slate-500 ml-auto">{c.cert_status}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="jury-submit" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold">Convoquer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// PORTAGE CONTRACTS
// ═══════════════════════════════════════════════════════════

function PortageContractsList({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portage/contracts", { params: { role: "certificateur" } });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const onActivate = async (contract_id) => {
    try {
      await api.post(`/portage/contracts/${contract_id}/activate`);
      toast.success("Contrat active");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const onDelete = async (contract_id) => {
    if (!window.confirm("Mettre fin au contrat ?")) return;
    try {
      await api.delete(`/portage/contracts/${contract_id}`);
      toast.success("Contrat termine");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <ListSkeleton items={3} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-400" data-testid="contracts-count">{items.length} contrat{items.length > 1 ? "s" : ""}</div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold" data-testid="contract-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau contrat
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 bg-[#14101c] rounded-xl border border-white/10">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 mb-2">Aucun contrat de portage</p>
          <p className="text-xs text-slate-500">Creez un contrat pour partager vos certifications avec un centre partenaire.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="contracts-list">
          {items.map(c => (
            <div key={c.contract_id} className="bg-[#14101c] border border-white/10 rounded-xl p-5 hover:border-[#f7c935]/30 transition-colors" data-testid={`contract-row-${c.contract_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={STATUS_BADGES[c.status]?.color || ""}>{STATUS_BADGES[c.status]?.label || c.status}</Badge>
                    {c.commission_type === "percentage" ? (
                      <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30">{c.commission_rate}% commission</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">{c.commission_amount_fixed} € / inscription</Badge>
                    )}
                  </div>
                  <h3 className="font-semibold text-white">{c.partner_name || c.partner_tenant_id}</h3>
                  <p className="text-xs text-slate-500 mt-1">{c.certifications_count || 0} certification{(c.certifications_count || 0) > 1 ? "s" : ""} portee{(c.certifications_count || 0) > 1 ? "s" : ""} · Du {c.start_date}{c.end_date ? ` au ${c.end_date}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Inscriptions</div>
                    <div className="text-lg font-bold text-white">{c.inscriptions_count || 0}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Commission</div>
                    <div className="text-lg font-bold text-emerald-400">{(c.total_commission_eur || 0).toLocaleString("fr-FR")} €</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {c.status === "draft" && (
                      <Button size="sm" onClick={() => onActivate(c.contract_id)} data-testid={`contract-activate-${c.contract_id}`} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Activer
                      </Button>
                    )}
                    {c.status !== "ended" && (
                      <Button size="sm" variant="ghost" onClick={() => onDelete(c.contract_id)} className="text-red-400" data-testid={`contract-end-${c.contract_id}`}>
                        <X className="w-3.5 h-3.5 mr-1" /> Terminer
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PortageContractFormDialog api={api} open={showCreate} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />
    </div>
  );
}

function PortageContractFormDialog({ api, open, onClose, onSaved }) {
  const [partners, setPartners] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [certifications, setCertifications] = useState([]);
  const [form, setForm] = useState({
    partner_tenant_id: "", certifications_portees: [],
    commission_type: "percentage", commission_rate: 15, commission_amount_fixed: 0,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "", notes: "", auto_invoice_enabled: true, invoice_day_of_month: 5,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/portage/partners-search", { params: { q: partnerSearch } }).then(r => setPartners(r.data || [])).catch(() => setPartners([]));
    api.get("/certifications").then(r => setCertifications(r.data || [])).catch(() => setCertifications([]));
  }, [open, partnerSearch, api]);

  const toggleCert = (cert_id) => {
    setForm(f => ({
      ...f,
      certifications_portees: f.certifications_portees.includes(cert_id)
        ? f.certifications_portees.filter(x => x !== cert_id)
        : [...f.certifications_portees, cert_id],
    }));
  };

  const submit = async () => {
    if (!form.partner_tenant_id) { toast.error("Selectionnez un partenaire"); return; }
    if (form.certifications_portees.length === 0) { toast.error("Selectionnez au moins 1 certification"); return; }
    setSaving(true);
    try {
      await api.post("/portage/contracts", form);
      toast.success("Contrat cree (statut brouillon)");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#14101c] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white">Nouveau contrat de portage</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Centre partenaire *</label>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input placeholder="Rechercher un tenant..." value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} className="pl-10 bg-[#1a1625] border-white/10" />
            </div>
            <div className="max-h-40 overflow-y-auto border border-white/10 rounded-md">
              {partners.map(p => (
                <button
                  key={p.tenant_id}
                  onClick={() => setForm(f => ({ ...f, partner_tenant_id: p.tenant_id }))}
                  data-testid={`partner-pick-${p.tenant_id}`}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 transition-colors ${form.partner_tenant_id === p.tenant_id ? "bg-[#f7c935]/10 text-[#f7c935]" : "text-slate-300"}`}
                >
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.tenant_id}{p.siret ? ` · SIRET ${p.siret}` : ""}</div>
                </button>
              ))}
              {partners.length === 0 && <p className="text-xs text-slate-500 p-3 text-center">Aucun tenant trouve</p>}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Certifications portees *</label>
            <div className="max-h-48 overflow-y-auto border border-white/10 rounded-md">
              {certifications.map(c => (
                <label
                  key={c.certification_id}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer text-sm"
                  data-testid={`cert-pick-${c.certification_id}`}
                >
                  <input type="checkbox" checked={form.certifications_portees.includes(c.certification_id)} onChange={() => toggleCert(c.certification_id)} className="accent-[#f7c935]" />
                  <span className="text-xs text-slate-500 font-mono w-24">{c.code}</span>
                  <span className="text-slate-200 flex-1">{c.title}</span>
                  {c.prix_certificateur_eur > 0 && <span className="text-xs text-emerald-400">{c.prix_certificateur_eur.toLocaleString("fr-FR")} €</span>}
                </label>
              ))}
              {certifications.length === 0 && <p className="text-xs text-slate-500 p-3 text-center">Aucune certification disponible — creez-en une d'abord.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Type de commission</label>
              <Select value={form.commission_type} onValueChange={v => setForm(f => ({ ...f, commission_type: v }))}>
                <SelectTrigger className="bg-[#1a1625] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1625] border-white/10">
                  <SelectItem value="percentage">Pourcentage</SelectItem>
                  <SelectItem value="fixed_amount">Montant fixe / inscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.commission_type === "percentage" ? (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Taux (%)</label>
                <Input data-testid="commission-rate" type="number" step="0.5" value={form.commission_rate} onChange={e => setForm(f => ({ ...f, commission_rate: parseFloat(e.target.value) || 0 }))} className="bg-[#1a1625] border-white/10" />
              </div>
            ) : (
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Montant (€ HT)</label>
                <Input data-testid="commission-fixed" type="number" step="0.01" value={form.commission_amount_fixed} onChange={e => setForm(f => ({ ...f, commission_amount_fixed: parseFloat(e.target.value) || 0 }))} className="bg-[#1a1625] border-white/10" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date de debut</label>
              <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="bg-[#1a1625] border-white/10" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Date de fin (optionnel)</label>
              <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="bg-[#1a1625] border-white/10" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Notes / clauses specifiques</label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="bg-[#1a1625] border-white/10" />
          </div>

          <div className="bg-[#1a1625] rounded-md p-3 border border-white/5 flex items-center gap-3">
            <input type="checkbox" checked={form.auto_invoice_enabled} onChange={e => setForm(f => ({ ...f, auto_invoice_enabled: e.target.checked }))} className="accent-[#f7c935]" />
            <span className="text-sm text-slate-300">Facturation automatique mensuelle des commissions</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving} data-testid="contract-save" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold">Creer le contrat (brouillon)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// INSCRIPTIONS REMONTEES
// ═══════════════════════════════════════════════════════════

function PortageInscriptionsView({ api }) {
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([
        api.get("/portage/inscriptions/summary", { params: { month_key: month } }),
        api.get("/portage/inscriptions", { params: { month_key: month } }),
      ]);
      setSummary(s.data);
      setItems(i.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, [api, month]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="space-y-4"><KpiSkeleton count={3} /><ListSkeleton items={4} /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-xs text-slate-400">Mois</label>
        <Input data-testid="month-filter" type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44 bg-[#14101c] border-white/10" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="summary-kpis">
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
          <div className="text-xs text-slate-500 mb-1">Inscriptions remontees</div>
          <div className="text-2xl font-bold text-white">{summary?.total_inscriptions || 0}</div>
        </div>
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
          <div className="text-xs text-slate-500 mb-1">Commissions a facturer (HT)</div>
          <div className="text-2xl font-bold text-[#f7c935]">{(summary?.total_commission_eur || 0).toLocaleString("fr-FR")} €</div>
        </div>
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-4">
          <div className="text-xs text-slate-500 mb-1">Centres partenaires</div>
          <div className="text-2xl font-bold text-white">{summary?.rows?.length || 0}</div>
        </div>
      </div>

      {(summary?.rows || []).length > 0 && (
        <div className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden">
          <div className="text-sm font-semibold text-white px-4 py-3 border-b border-white/10">Synthese par contrat — {month}</div>
          <table className="w-full text-sm" data-testid="contracts-summary-table">
            <thead className="bg-[#1a1625] text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Partenaire</th>
                <th className="text-right px-4 py-2 font-semibold">Inscriptions</th>
                <th className="text-right px-4 py-2 font-semibold">Tarif total HT</th>
                <th className="text-right px-4 py-2 font-semibold">Commission HT</th>
                <th className="text-center px-4 py-2 font-semibold">Statut facture</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map(r => (
                <tr key={r.contract_id} className="border-t border-white/5">
                  <td className="px-4 py-3 text-slate-200">{r.partner_name}</td>
                  <td className="px-4 py-3 text-right text-white">{r.inscriptions_count}</td>
                  <td className="px-4 py-3 text-right text-slate-400">{r.tarif_total_eur.toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-3 text-right text-emerald-400 font-semibold">{r.commission_total_eur.toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-3 text-center">
                    {r.all_invoiced ? (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">Facture</Badge>
                    ) : (
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30">A facturer</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden">
          <div className="text-sm font-semibold text-white px-4 py-3 border-b border-white/10">Detail des inscriptions ({items.length})</div>
          <table className="w-full text-sm" data-testid="inscriptions-table">
            <thead className="bg-[#1a1625] text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Stagiaire</th>
                <th className="text-left px-4 py-2 font-semibold">Centre</th>
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-right px-4 py-2 font-semibold">Tarif</th>
                <th className="text-right px-4 py-2 font-semibold">Commission</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.portage_inscription_id} className="border-t border-white/5">
                  <td className="px-4 py-3"><div className="text-slate-200">{i.student_name}</div><div className="text-xs text-slate-500">{i.student_email}</div></td>
                  <td className="px-4 py-3 text-slate-400">{i.partner_name}</td>
                  <td className="px-4 py-3 text-slate-400">{(i.inscription_date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right text-slate-300">{i.tarif_pratique_eur?.toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-3 text-right text-emerald-400 font-semibold">{i.commission_eur?.toLocaleString("fr-FR")} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-20 bg-[#14101c] rounded-xl border border-white/10">
          <Users className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400">Aucune inscription remontee pour {month}</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// FACTURES COMMISSIONS
// ═══════════════════════════════════════════════════════════

function PortageInvoicesView({ api }) {
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/portage/invoices", { params: { role: "certificateur" } });
      setItems(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!window.confirm(`Generer les factures de commission pour le mois ${month} ?`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/portage/invoices/generate", { month_key: month });
      toast.success(`${data.created_count} facture${data.created_count > 1 ? "s" : ""} generee${data.created_count > 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setGenerating(false);
  };

  const send = async (invoice_id) => {
    try {
      await api.post(`/portage/invoices/${invoice_id}/send`);
      toast.success("Facture envoyee");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const markPaid = async (invoice_id) => {
    try {
      await api.post(`/portage/invoices/${invoice_id}/mark-paid`);
      toast.success("Facture marquee payee");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading) return <ListSkeleton items={4} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-400">Mois a facturer</label>
          <Input data-testid="invoice-month" type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44 bg-[#14101c] border-white/10" />
        </div>
        <Button onClick={generate} disabled={generating} data-testid="generate-invoices-btn" className="bg-[#f7c935] hover:bg-[#e5b825] text-black font-semibold">
          <Receipt className="w-4 h-4 mr-2" /> Generer les factures du mois
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 bg-[#14101c] rounded-xl border border-white/10">
          <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400 mb-2">Aucune facture de commission</p>
          <p className="text-xs text-slate-500">Generez les factures du mois en cliquant sur le bouton ci-dessus.</p>
        </div>
      ) : (
        <div className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-[#1a1625] text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">N° facture</th>
                <th className="text-left px-4 py-2 font-semibold">Partenaire</th>
                <th className="text-left px-4 py-2 font-semibold">Mois</th>
                <th className="text-right px-4 py-2 font-semibold">Inscriptions</th>
                <th className="text-right px-4 py-2 font-semibold">Total HT</th>
                <th className="text-right px-4 py-2 font-semibold">TTC</th>
                <th className="text-center px-4 py-2 font-semibold">Statut</th>
                <th className="text-center px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(inv => (
                <tr key={inv.portage_invoice_id} className="border-t border-white/5" data-testid={`invoice-row-${inv.portage_invoice_id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#f7c935]">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-slate-200">{inv.partner_name}</td>
                  <td className="px-4 py-3 text-slate-400">{inv.month_key}</td>
                  <td className="px-4 py-3 text-right text-white">{inv.inscriptions_count}</td>
                  <td className="px-4 py-3 text-right text-emerald-400 font-semibold">{inv.commission_total_ht_eur.toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-3 text-right text-slate-300">{inv.total_ttc_eur.toLocaleString("fr-FR")} €</td>
                  <td className="px-4 py-3 text-center">
                    <Badge className={STATUS_BADGES[inv.status]?.color || ""}>{STATUS_BADGES[inv.status]?.label || inv.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {inv.status === "draft" && <Button size="sm" variant="ghost" onClick={() => send(inv.portage_invoice_id)} data-testid={`invoice-send-${inv.portage_invoice_id}`}><Send className="w-3.5 h-3.5" /></Button>}
                      {inv.status === "sent" && <Button size="sm" variant="ghost" onClick={() => markPaid(inv.portage_invoice_id)} data-testid={`invoice-paid-${inv.portage_invoice_id}`} className="text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
